/**
 * Calendar API: fetch month data from backend. Used by useCalendarData.
 *
 * Response shapes:
 * - Legacy: `data.calendar[0]` — object keyed by date → `{ state?, icon? }`
 * - List: `data.days[]` — per-day rows with `status`, `icon`, `has_menu`, `is_frozen`, …
 *
 * **`deriveCalendarMonthFromApi`** merges both: calendar object first, then **`days` overlays**
 * the same normalized date so list rows can refine or replace entries.
 */
import { apiGet } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { normalizeCalendarDate as normalizeCalendarDateUtil } from '../../../shared/utils/dateUtils';
import { unwrapEnvelopeOrRaw } from '../../query/normalizers/unwrapEnvelope';
import type { CalendarDay as ApiCalendarDay } from './apiTypes';

export const normalizeCalendarDate = normalizeCalendarDateUtil;

export type CalendarDateEntry = { state?: string; icon?: string };
export type CalendarMonthMap = Record<string, CalendarDateEntry>;

export interface CalendarApiData {
  calendar?: CalendarMonthMap[];
  days?: ApiCalendarDay[];
  month?: string;
}

export type CalendarFetchResult = { data?: CalendarApiData };

export async function fetchCalendarMonth(
  customer_id: number,
  month: string,
): Promise<CalendarFetchResult> {
  const month_ascii = ensureAsciiDateString(month);
  const raw = await apiGet<unknown>(endpoints.calendar, { customer_id, month: month_ascii });
  const data = unwrapEnvelopeOrRaw<CalendarApiData>(raw);
  return { data };
}

export type CalendarDateState = 'Booked Meal' | 'Partially Booked Meal' | 'Select Meal' | 'On the way' | 'Skipped' | 'Feedback';

function normalizeApiState(api_state: string | undefined): CalendarDateState | undefined {
  if (!api_state || typeof api_state !== 'string') {
    return undefined;
  }
  const s = api_state.trim();
  if (s === 'Booked Meal') {return 'Booked Meal';}
  if (s === 'Partially Booked Meal') {return 'Partially Booked Meal';}
  if (s === 'Select Meal') {return 'Select Meal';}
  if (s === 'On the way') {return 'On the way';}
  if (s === 'Skipped') {return 'Skipped';}
  if (s === 'Feedback') {return 'Feedback';}
  if (s === 'Day Off') {return undefined;}
  return undefined;
}

export function mapApiDayToState(api_day: ApiCalendarDay): CalendarDateState | undefined {
  const status = typeof api_day.status === 'string' ? api_day.status.toLowerCase().trim() : '';
  const icon = typeof api_day.icon === 'string' ? api_day.icon.toLowerCase() : '';
  if (status === 'delivered' || status === 'booked' || status === 'ordered' || status === 'completed' || status === 'fulfilled') {return 'Booked Meal';}
  if (status === 'partially_delivered' || status === 'partial' || status === 'partially_booked') {return 'Partially Booked Meal';}
  if (status === 'pending') {return 'On the way';}
  if (status === 'skipped') {return 'Skipped';}
  if (status === 'feedback') {return 'Feedback';}
  if (icon.includes('check') && (icon.includes('circle') || icon.includes('fa-check'))) {return 'Booked Meal';}
  if (icon.includes('partial')) {return 'Partially Booked Meal';}
  if (api_day.has_menu) {return 'Select Meal';}
  return undefined;
}

export interface DerivedCalendarMonth {
  stateByDate: Map<string, CalendarDateState>;
  iconByDate: Map<string, string>;
  frozenDates: string[];
}

/**
 * Single pass over API payload: state map, icon map, frozen dates from `days[]`.
 * `calendar[0]` seeds maps; `days` entries overlay by normalized date.
 */
export function deriveCalendarMonthFromApi(data: CalendarApiData | undefined): DerivedCalendarMonth {
  const stateByDate = new Map<string, CalendarDateState>();
  const iconByDate = new Map<string, string>();
  const frozen: string[] = [];

  const applyDayRow = (d: ApiCalendarDay) => {
    const normalized_date = normalizeCalendarDate(d.date);
    if (!normalized_date) {return;}
    const state = mapApiDayToState(d);
    if (state) {stateByDate.set(normalized_date, state);}
    if (typeof d.icon === 'string' && d.icon.trim()) {iconByDate.set(normalized_date, d.icon.trim());}
    if (Boolean((d as { is_frozen?: boolean | number | string }).is_frozen)) {
      frozen.push(normalized_date);
    }
  };

  if (data?.calendar?.length && typeof data.calendar[0] === 'object' && data.calendar[0] !== null) {
    const month_obj = data.calendar[0] as Record<string, CalendarDateEntry>;
    for (const date of Object.keys(month_obj)) {
      const normalized = normalizeCalendarDate(date);
      if (!normalized) {continue;}
      const entry = month_obj[date];
      const state = normalizeApiState(entry?.state);
      if (state) {stateByDate.set(normalized, state);}
      if (typeof entry?.icon === 'string' && entry.icon.trim()) {iconByDate.set(normalized, entry.icon.trim());}
    }
  }

  if (Array.isArray(data?.days)) {
    for (const d of data.days) {
      applyDayRow(d);
    }
  }

  return {
    stateByDate,
    iconByDate,
    frozenDates: [...new Set(frozen)],
  };
}

export function buildCalendarStateMap(days: ApiCalendarDay[] | undefined): Map<string, CalendarDateState> {
  const map = new Map<string, CalendarDateState>();
  if (!Array.isArray(days)) {return map;}
  for (const d of days) {
    const normalized_date = normalizeCalendarDate(d.date);
    if (!normalized_date) {continue;}
    const state = mapApiDayToState(d);
    if (state) {map.set(normalized_date, state);}
  }
  return map;
}

export function buildCalendarIconMap(days: ApiCalendarDay[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!Array.isArray(days)) {return map;}
  for (const d of days) {
    const normalized_date = normalizeCalendarDate(d.date);
    if (normalized_date && d.icon) {map.set(normalized_date, d.icon);}
  }
  return map;
}
